/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type {
  TeamPaymentCutoffSource,
  TeamPaymentStatus,
  TeamPaymentStatusCode,
} from '@sportninja/common/services/teamBalance'
import RichTooltip from 'src/components/ts-components/RichTooltip'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import { balanceLabel, balanceText } from './../utils/balanceI18n'
// Shared with the header's standing copy — see the `on` vs `at` rule there.
import { formatCutoff } from './../utils/balanceCutoff'

/**
 * SN-4709 follow-up — the TEAM's payment status, for the rep and the admin.
 *
 * Deliberately NOT an extension of BalanceStatusChip: that chip describes ONE
 * SLOT's lifecycle and its dot colours drive the STATUS column legend. This
 * describes the whole registration and lives beside the roster switcher.
 *
 * Display only — "Not Qualified" tells a rep they are short of the qualifying
 * threshold; it does not block roster, scheduling or play.
 *
 * Text PLUS icon, never colour alone: the five states have to be distinguishable
 * in greyscale and to anyone who cannot separate the red from the green.
 */

type StatusPresentation = {
  /** null renders the plain dot — Qualified has a circle, no glyph. */
  icon: ReactNode | null
  color: string
  labelKey: string
  tipKey: string
  nextKey: string
  /** Which cutoff the copy should name, if any. */
  cutoff: 'qualification' | 'final' | null
}

/**
 * SN-8310 (Sep 22) — ONE colour per status, shared by the pill, the status
 * legend and the header banner, so they cannot drift apart.
 *
 *  - Qualified — Balance Due is GREEN, like Paid in Full (Inon, Sep 22 —
 *    reversing the same day's amber pick): once the minimum is in, the spot is
 *    secured. The label and the banner's sentence still say what is owed; the
 *    pill's dot and the banner's check tell the two green states apart.
 *  - Both past-due states are RED, as ERROR_100: ERROR_200 text on the badge
 *    surface is 3.2:1 and fails AA; ERROR_100 passes at 4.8:1. Balance Overdue
 *    used to be ORANGE, which protanopic readers cannot tell from the green
 *    beside it. The icons still carry the difference: a stop sign for a missed
 *    minimum, a warning for an overdue balance.
 */
export const PRESENTATION: Record<TeamPaymentStatusCode, StatusPresentation> = {
  minimum_payment_due: {
    icon: <ClockCircleOutlined aria-hidden='true' />,
    color: colors.WARNING_300,
    labelKey: 'common:teamPaymentStatusMinimumPaymentDue',
    tipKey: 'common:teamPaymentStatusTipMinimumPaymentDue',
    nextKey: 'common:teamPaymentStatusNextMinimumPaymentDue',
    cutoff: 'qualification',
  },
  not_qualified: {
    icon: <StopOutlined aria-hidden='true' />,
    color: colors.ERROR_100,
    labelKey: 'common:teamPaymentStatusNotQualified',
    tipKey: 'common:teamPaymentStatusTipNotQualified',
    nextKey: 'common:teamPaymentStatusNextNotQualified',
    cutoff: 'qualification',
  },
  qualified_balance_due: {
    icon: null,
    color: colors.SUCCESS_200,
    labelKey: 'common:teamPaymentStatusQualifiedBalanceDue',
    tipKey: 'common:teamPaymentStatusTipQualifiedBalanceDue',
    nextKey: 'common:teamPaymentStatusNextQualifiedBalanceDue',
    cutoff: 'final',
  },
  balance_overdue: {
    icon: <WarningOutlined aria-hidden='true' />,
    color: colors.ERROR_100,
    labelKey: 'common:teamPaymentStatusBalanceOverdue',
    tipKey: 'common:teamPaymentStatusTipBalanceOverdue',
    nextKey: 'common:teamPaymentStatusNextBalanceOverdue',
    cutoff: 'final',
  },
  paid_in_full: {
    icon: <CheckCircleOutlined aria-hidden='true' />,
    color: colors.SUCCESS_200,
    labelKey: 'common:teamPaymentStatusPaidInFull',
    tipKey: 'common:teamPaymentStatusTipPaidInFull',
    nextKey: 'common:teamPaymentStatusNextPaidInFull',
    cutoff: null,
  },
}

/**
 * The FINAL cutoff is max(registration end, competition start), so the same
 * status can be governed by either date — and which one it is explains *why*
 * that date applies. The server says which; without using it the tooltip can
 * only say a generic "due by", which reads as wrong when the winning source is
 * the surprising one (a registration window that outlasts the season start).
 *
 * The qualification cutoff has only one possible source, so its copy needs no
 * variant. An unrecognized source falls back to the generic key rather than
 * building a translation key that does not exist.
 */
const FINAL_SOURCE_SUFFIX: Record<TeamPaymentCutoffSource, string> = {
  registration_end: 'RegistrationEnd',
  competition_start: 'CompetitionStart',
}

const nextCopyKey = (
  presentation: StatusPresentation,
  status: TeamPaymentStatus
): string => {
  if (presentation.cutoff !== 'final') {
    return presentation.nextKey
  }
  const suffix =
    FINAL_SOURCE_SUFFIX[
      status.cutoffs?.final?.source as TeamPaymentCutoffSource
    ]

  return suffix ? `${presentation.nextKey}${suffix}` : presentation.nextKey
}

type TeamPaymentStatusBadgeProps = {
  status?: TeamPaymentStatus | null
  /** Optional division name, for the All-Divisions list where one badge is ambiguous. */
  divisionName?: string
}

const TeamPaymentStatusBadge = ({
  status,
  divisionName,
}: TeamPaymentStatusBadgeProps) => {
  // Null status (D10: no threshold / no registration end / no competition start)
  // and any code this build does not know render NOTHING — a badge that cannot be
  // explained is worse than no badge.
  const presentation = status
    ? PRESENTATION[status.code as TeamPaymentStatusCode]
    : undefined

  if (!presentation) {
    return null
  }

  const label = balanceLabel(presentation.labelKey)
  const date = presentation.cutoff
    ? formatCutoff(status?.cutoffs?.[presentation.cutoff])
    : null
  // NoDate variants hang off the BASE key, not the source-specific one: with no
  // usable date there is no source worth naming either.
  const next = date
    ? balanceText(nextCopyKey(presentation, status as TeamPaymentStatus), {
        date,
      })
    : balanceText(`${presentation.nextKey}NoDate`)

  return (
    <RichTooltip
      title={label}
      body={balanceText(presentation.tipKey)}
      next={next}
    >
      <span
        css={badgeStyle(presentation.color)}
        data-testid={`team-payment-status-${status?.code}`}
      >
        {presentation.icon ?? <span css={dotStyle(presentation.color)} />}
        <span>{divisionName ? `${divisionName} — ${label}` : label}</span>
      </span>
    </RichTooltip>
  )
}

export default TeamPaymentStatusBadge

/**
 * James, Jul 31: "make that a bit bigger and more prominent… we want them to
 * really know they haven't met the threshold." This badge is the ONE thing on
 * the page that says a team has not qualified, and at 12px in a 4px pill it read
 * as a passive tag beside the roster switcher rather than a call to action.
 * One type step up (14px), a fuller pill and a bigger dot — no colour change,
 * because the hue already carries the severity.
 */
const badgeStyle = (color: string) => css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${color};
  background: ${colors.SECONDARY_300};
  color: ${color};
  white-space: nowrap;
  ${paragraphSmall}
  ${weightSemiBold}
`

const dotStyle = (color: string) => css`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${color};
`
